import React, { createContext, useState, useCallback, useEffect, useRef, useContext, useMemo } from "react";
import { fetchBannerData } from "../api/AdminBanner/AdminBannerDataInfo";
import { AuthContext } from "./AuthContext";
 
export const BannerDetailsContext = createContext();
 
/**
 * BannerDetailsProvider provides global state for banner details and visibility.
 * @param {object} props
 * @param {React.ReactNode} props.children - Child components consuming the context.
 */
export const BannerDetailsProvider = ({ children }) => {
  const { user } = useContext(AuthContext);
  const [bannerColor, setBannerColor] = useState("#002677");
  const [banner_text_color, setBannerTextColor] = useState("#ffffff");
  const [isBannerActive, setIsBannerActive] = useState(false);
  const [bannerMessage, setBannerMessage] = useState("");
  const [ExistingBannerCreatorID, setExistingBannerCreatorID] = useState("");
  const [resetBannerTrigger, setResetBannerTrigger] = useState(0);
  const BANNER_REFRESH_KEY = 'eva_banner_refresh';
  const [showAnnouncementBanner, setShowAnnouncementBanner] = useState(false);
  // Cross-tab signaling: uses localStorage to notify other tabs to reload banner details
  // Scheduled timers for auto-update at start/end times
  const scheduledStartTimerRef = useRef(null);
  const scheduledEndTimerRef = useRef(null);
  // Ref to hold the latest loadBannerDetails for use in scheduled timers
  const loadBannerDetailsRef = useRef(null);
  // Store banner data for client-side time checks
  const bannerDataRef = useRef(null);
  // Track if initial API call has been made to prevent duplicate calls on mount
  const hasInitializedRef = useRef(false);
  // Track if an API call is currently in progress to prevent concurrent calls
  const isLoadingRef = useRef(false);
  
  // Check if current time is within scheduled window
  const isBannerWithinScheduledTime = (startTime, endTime) => {
    if (!startTime && !endTime) return null;
    const now = Date.now();
    const startMs = startTime ? new Date(startTime).getTime() : 0;
    const endMs = endTime ? new Date(endTime).getTime() : Infinity;
    return now >= startMs && now < endMs;
  };
  
  // Helper to clear scheduled timers
  const clearScheduledTimers = useCallback(() => {
    if (scheduledStartTimerRef.current) {
      clearTimeout(scheduledStartTimerRef.current);
      scheduledStartTimerRef.current = null;
    }
    if (scheduledEndTimerRef.current) {
      clearTimeout(scheduledEndTimerRef.current);
      scheduledEndTimerRef.current = null;
    }
  }, []);
  
  // Schedule banner auto-refresh at start/end times
  const scheduleAutoRefresh = useCallback((data) => {
    // Clear any existing scheduled timers first
    clearScheduledTimers();
    
    if (!data) return;
    
    const now = Date.now();
    const MAX_TIMER_DELAY = 2147483647; // Max safe setTimeout value (~24.8 days)
    
    // Handler to update banner immediately based on client-side time
    const handleTimeEvent = (shouldBeActive) => {
      const d = bannerDataRef.current;
      if (d) {
        setIsBannerActive(shouldBeActive);
        setBannerColor(shouldBeActive ? (d.banner_ribbon_color_code || "#002677") : "#002677");
        setBannerTextColor(shouldBeActive ? (d.banner_text_color || "#ffffff") : "#ffffff");
        setBannerMessage(shouldBeActive ? (d.banner_message || "") : "");
      }
    };
    
    // Schedule refresh at start time
    if (data.banner_display_start_time) {
      const startTime = new Date(data.banner_display_start_time).getTime();
      const delayUntilStart = startTime - now;
      
      if (delayUntilStart > 0 && delayUntilStart <= MAX_TIMER_DELAY) {
        scheduledStartTimerRef.current = setTimeout(() => handleTimeEvent(true), delayUntilStart);
      }
    }
    
    // Schedule refresh at end time
    if (data.banner_display_end_time) {
      const endTime = new Date(data.banner_display_end_time).getTime();
      const delayUntilEnd = endTime - now;
      
      if (delayUntilEnd > 0 && delayUntilEnd <= MAX_TIMER_DELAY) {
        scheduledEndTimerRef.current = setTimeout(() => handleTimeEvent(false), delayUntilEnd);
      }
    }
  }, [clearScheduledTimers]);
  
  const loadBannerDetails = useCallback(async () => {
    // Do not fetch banner data if on the smart-answer page
    if (window.location.pathname.startsWith('/smart-answer')) {
      setIsBannerActive(false); // Ensure banner is explicitly off
      return null;
    }
   
    // Wait for authentication - check if user is available and access token exists
    let accessToken = null;
    if (typeof sessionStorage !== 'undefined') {
      try {
        accessToken = sessionStorage.getItem('access_token');
      } catch (e) {
        console.warn('BannerDetailsContext: Unable to access sessionStorage for access_token', e);
        accessToken = null;
      }
    }
    if (!user || !accessToken) {
      return null;
    }
    // Prevent concurrent API calls
    if (isLoadingRef.current) {
      return null;
    }
    
    isLoadingRef.current = true;
   
    try {
      const data = await fetchBannerData();
      if (data) {
        bannerDataRef.current = data;
        // Use client-side time check if scheduled, otherwise use server flag
        const withinSchedule = isBannerWithinScheduledTime(data.banner_display_start_time, data.banner_display_end_time);
        const serverActive = data.is_banner_active === true || data.is_banner_active === "true";
        const isActive = withinSchedule !== null ? withinSchedule : serverActive;
        
        setIsBannerActive(isActive);
        setBannerColor(isActive ? (data.banner_ribbon_color_code || "#002677") : "#002677");
        setBannerTextColor(isActive ? (data.banner_text_color || "#ffffff") : "#ffffff");
        setBannerMessage(isActive ? (data.banner_message || "") : "");
        setExistingBannerCreatorID(data.create_userid || "");
        scheduleAutoRefresh(data);
        return data;
      }
      // Fallback defaults when no data
      setIsBannerActive(false);
      setBannerColor("#002677");
      setBannerTextColor("#ffffff");
      setBannerMessage("");
      setExistingBannerCreatorID("");
      return null;
    } catch (e) {
      console.error('BannerDetailsContext: Failed to fetch banner data', e);
      setIsBannerActive(false);
      setBannerColor("#002677");
      setBannerTextColor("#ffffff");
      setBannerMessage("");
      setExistingBannerCreatorID("");
      return null;
    } finally {
      isLoadingRef.current = false;
    }
  }, [user, scheduleAutoRefresh]);
  
  // Keep the ref updated so scheduled timers can access the latest loadBannerDetails
  useEffect(() => {
    loadBannerDetailsRef.current = loadBannerDetails;
  }, [loadBannerDetails]);
  
  /**
   * Triggers banner refresh across tabs via localStorage event.
   * Note: Does NOT call loadBannerDetails() directly to avoid duplicate API calls.
   * The calling component (e.g., BannerAdminPage) already loads banner data via updateBannerUI().
   * This function only signals other browser tabs to refresh their banner data.
   */
  const triggerBannerRefresh = useCallback(() => {
    try {
      localStorage.setItem(BANNER_REFRESH_KEY, Date.now().toString());
    } catch (e) {
      console.error('Banner refresh signal failed:', e);
      // Optionally, set a state to show error to user
      setBannerMessage('Failed to signal banner refresh. Please try again.');
    }
    setResetBannerTrigger(prev => prev + 1);
  }, []);
  useEffect(() => {
    function handleStorage(e) {
      if (e.key === BANNER_REFRESH_KEY) loadBannerDetailsRef.current?.();
    }
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);
  useEffect(() => {
    // Only load banner details after user authentication is complete (one-time initial load)
     let hasToken = false;
     if (typeof sessionStorage !== 'undefined') {
       try {
         hasToken = !!sessionStorage.getItem('access_token');
       } catch (e) {
         console.warn('BannerDetailsContext: Unable to access sessionStorage for access_token in useEffect', e);
         hasToken = false;
       }
     }
     if (user && hasToken && !hasInitializedRef.current) {
      hasInitializedRef.current = true;
      loadBannerDetails();
    }
    // Reset initialization flag when user logs out or token is missing,
    // so a new session can re-run banner initialization.
    if (!user || !hasToken) {
      hasInitializedRef.current = false;
      clearScheduledTimers(); // Clear any active timers when user logs out
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);
  // Cleanup scheduled timers on unmount
  useEffect(() => {
    return () => {
      clearScheduledTimers();
    };
  }, [clearScheduledTimers]);
  const resetBanner = () => { setResetBannerTrigger(prev => prev + 1); };
  const contextValue = useMemo(() => ({
        bannerColor, setBannerColor,
        banner_text_color, setBannerTextColor,
        isBannerActive, setIsBannerActive,
        bannerMessage, setBannerMessage,
        ExistingBannerCreatorID, setExistingBannerCreatorID,
        loadBannerDetails, resetBanner, resetBannerTrigger, triggerBannerRefresh,
        showAnnouncementBanner, setShowAnnouncementBanner
   // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [bannerColor, banner_text_color, isBannerActive, bannerMessage, ExistingBannerCreatorID, resetBannerTrigger, showAnnouncementBanner]);
  return (
    <BannerDetailsContext.Provider value={contextValue}>
      {children}
    </BannerDetailsContext.Provider> 
  );
};